import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api, errMsg, TIPOS_CITA } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { TEXTO_BASE, rellenarTexto, setTextosRecordatorio } from "@/components/ApptDialog";

const TIPOS = ["evaluacion", "revision", "sesion_tv", "syntonic", "wivi", "tomatis", "otro"];
const EJEMPLO = { fecha: "2026-11-04", inicio: "17:00", patient: { nombre: "Lucía González" } };

const Centros = ({ c, setC }) => (
  <div className="grid gap-2 sm:grid-cols-2" data-testid="recordatorio-centros">
    {["MADRID", "ALCOBENDAS"].map((k) => (
      <div key={k} className="rounded border border-slate-200 p-2 space-y-1">
        <p className="text-xs font-semibold text-slate-700">{k === "MADRID" ? "Madrid" : "Alcobendas"}</p>
        <Input className="h-8 text-xs" placeholder="Dirección" value={c[k]?.direccion || ""} onChange={(e) => setC({ ...c, [k]: { ...c[k], direccion: e.target.value } })} data-testid={`recordatorio-dir-${k}`} />
        <Input className="h-8 text-xs" placeholder="Teléfono" value={c[k]?.telefono || ""} onChange={(e) => setC({ ...c, [k]: { ...c[k], telefono: e.target.value } })} data-testid={`recordatorio-tel-${k}`} />
      </div>
    ))}
  </div>
);

export const RecordatorioTextos = () => {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState({});
  const [c, setC] = useState({});
  const [ver, setVer] = useState("MADRID");
  const [tipo, setTipo] = useState("evaluacion");
  const [busy, setBusy] = useState(false);
  const abrir = async () => {
    const { data } = await api.get("/recordatorio-textos");
    setT(Object.fromEntries(TIPOS.map((k) => [k, data.textos[k] || TEXTO_BASE])));
    setC(data.centros);
    setOpen(true);
  };
  const guardar = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/recordatorio-textos", { textos: Object.fromEntries(TIPOS.map((k) => [k, t[k] === TEXTO_BASE ? "" : t[k]])), centros: c });
      setTextosRecordatorio(data);
      toast.success("Textos de recordatorio guardados");
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <>
      <button type="button" onClick={abrir} data-testid="recordatorio-textos-btn" className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-normal text-slate-600 hover:bg-slate-50"><Pencil className="h-3 w-3" />Textos</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="recordatorio-textos-dialog">
          <DialogHeader><DialogTitle className="font-[Outfit]">Texto del recordatorio según el tipo de cita</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-1">
            {TIPOS.map((k) => (
              <button key={k} type="button" onClick={() => setTipo(k)} data-testid={`recordatorio-tipo-${k}`}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${tipo === k ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                {TIPOS_CITA[k].label}{t[k] && t[k] !== TEXTO_BASE ? " •" : ""}
              </button>
            ))}
          </div>
          <Textarea rows={6} value={t[tipo] || ""} onChange={(e) => setT({ ...t, [tipo]: e.target.value })} data-testid="recordatorio-texto" />
          <p className="text-[11px] text-slate-500">Se rellenan solos: <b>{"{paciente}"}</b> <b>{"{fecha}"}</b> <b>{"{hora}"}</b> <b>{"{profesional}"}</b> <b>{"{centro}"}</b> <b>{"{direccion}"}</b> <b>{"{telefono_centro}"}</b> <b>{"{tipo}"}</b>. La dirección y el teléfono salen del centro del paciente. El punto (•) indica los tipos con texto propio.</p>
          <Centros c={c} setC={setC} />
          <div className="flex items-center gap-1 text-[11px] text-slate-500">Vista previa para:
            {["MADRID", "ALCOBENDAS"].map((k) => <button key={k} type="button" onClick={() => setVer(k)} data-testid={`recordatorio-ver-${k}`} className={`rounded px-1.5 py-0.5 ${ver === k ? "bg-emerald-100 text-emerald-800" : "hover:bg-slate-100"}`}>{k === "MADRID" ? "Madrid" : "Alcobendas"}</button>)}
          </div>
          <div className="rounded bg-emerald-50 p-2 text-xs text-emerald-900 whitespace-pre-line" data-testid="recordatorio-preview">{rellenarTexto(t[tipo] || "", { ...EJEMPLO, tipo, patient: { ...EJEMPLO.patient, centro: ver } }, { nombre: "Ana" }, c)}</div>
          <div className="flex justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={() => setT({ ...t, [tipo]: TEXTO_BASE })} data-testid="recordatorio-texto-base">Volver al texto base</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button disabled={busy} onClick={guardar} data-testid="recordatorio-textos-guardar" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
